// {{WORKTREE_ROOT}}/picosponsorconsole/packages/design/src/i18n.tsx
// PLAN2 WP09 — the design package is framework-agnostic: it never imports the console's locale.ts. The host app
// provides t() once at its root (console WP10: `t: ([section, id], args) => t(section, id, args)`; site WP11 the same
// over the site locale), plus lang and dir. Rendering a component without the provider throws — a missing provider
// must never fall back to printing a locale id on screen (C-133).
import { createContext, useContext, type ReactNode } from "react";
import type { Msg } from "./strings.js";

export interface DesignI18n {
  t: (msg: Msg, args?: Record<string, unknown>) => string;
  lang: string;
  dir: "ltr" | "rtl";
}

const DesignI18nContext = createContext<DesignI18n | null>(null);

export function DesignI18nProvider({ value, children }: { value: DesignI18n; children: ReactNode }) {
  return <DesignI18nContext.Provider value={value}>{children}</DesignI18nContext.Provider>;
}

export function useDesignI18n(): DesignI18n {
  const value = useContext(DesignI18nContext);
  if (!value) throw new Error("@picosponsor/design: wrap the app in <DesignI18nProvider> (PLAN2 WP09 §6.2)");
  return value;
}

export function formatNumber(lang: string, n: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(lang, options).format(n);
}

/** Two-letter initials for Avatar: first letter of the first and last word, upper-cased in the UI locale. */
export function initials(name: string, lang: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const first = words[0][0] ?? "";
  const last = words.length > 1 ? words[words.length - 1][0] ?? "" : "";
  return (first + last).toLocaleUpperCase(lang);
}
